/* eslint-disable fp/no-mutation */
import { useEffect, useReducer, useRef, useState } from 'react'
import type MuxPlayerElement from '@mux/mux-player'
import { throttle } from 'lodash'
import {
  getRejectionError,
  postVideoProgress,
  postVideoStepCompleted,
} from 'src/v3/classroom-v2/utils/api'

// The interruption states are mutually exclusive by nature — a discriminated
// union with a reducer makes the impossible combinations (e.g. the countdown
// dialog and the insufficient-progress dialog both open at once)
// unrepresentable, rather than relying on independent booleans staying in
// sync by convention.
export type InterruptionState =
  | { kind: 'idle' }
  | { kind: 'multiTabDialog' }
  | { kind: 'countdown'; secondsRemaining: number }
  | { kind: 'insufficientProgressDialog' }
  | { kind: 'priorStepsIncompleteDialog' }

type InterruptionAction =
  | { type: 'multiTabConflictDetected' }
  | { type: 'confirmContinuePlayback' }
  | { type: 'countdownTick' }
  | { type: 'countdownElapsed' }
  | { type: 'insufficientProgressDetected' }
  | { type: 'acknowledgeInsufficientProgress' }
  | { type: 'priorStepsIncompleteDetected' }
  | { type: 'acknowledgePriorStepsIncomplete' }

let COUNTDOWN_SECONDS = 30

let interruptionReducer = (
  state: InterruptionState,
  action: InterruptionAction,
): InterruptionState => {
  switch (action.type) {
    case 'multiTabConflictDetected':
      return { kind: 'multiTabDialog' }
    case 'confirmContinuePlayback':
      return { kind: 'countdown', secondsRemaining: COUNTDOWN_SECONDS }
    case 'countdownTick':
      return state.kind === 'countdown'
        ? { kind: 'countdown', secondsRemaining: state.secondsRemaining - 1 }
        : state
    case 'countdownElapsed':
      return { kind: 'idle' }
    case 'insufficientProgressDetected':
      return { kind: 'insufficientProgressDialog' }
    case 'acknowledgeInsufficientProgress':
      return { kind: 'idle' }
    case 'priorStepsIncompleteDetected':
      return { kind: 'priorStepsIncompleteDialog' }
    case 'acknowledgePriorStepsIncomplete':
      return { kind: 'idle' }
    default:
      return state
  }
}

let PROGRESS_POST_INTERVAL_MS = 20_000
// Grace window before the end of the video within which reaching the end
// still counts as having watched it, carried over from v1.
let COMPLETION_THRESHOLD_SECONDS = 3
// Forward seeks beyond this many seconds past the last valid position are
// treated as a skip attempt and clamped back.
let MAX_FORWARD_SEEK_SECONDS = 1

export type UseVideoComplianceOptions = {
  courseID: string
  videoID: string
  // The step's previously-recorded max position, in seconds. Rehash gating:
  // only playback beyond this counts as new progress — replaying or
  // scrubbing within already-watched territory must not post anything, even
  // in compliance mode. See classroom-v2-wizard.slice.ts's design notes on
  // rehash gating composing with (not replacing) the mode gate.
  maxWatchedPositionSeconds: number
  isStepCompleted: boolean
  onCompleted: () => void
  onProgressMade: (progressSeconds: number) => void
  // Navigates the user back to the course's first incomplete step — the
  // recovery action for a prior-steps-incomplete rejection is different
  // from insufficient-progress: the problem isn't this video, so rewinding
  // position in it accomplishes nothing.
  onPriorStepsIncomplete: () => void
  playerRef: React.RefObject<MuxPlayerElement | null>
}

// A momentary notice, not an interruption — the video keeps playing, no
// player interaction is blocked, and it can co-occur with an interruption
// dialog (e.g. a seek attempt right as a multi-tab conflict resolves). This
// is why it's a separate piece of state rather than a case in the
// interruption union: those states are mutually exclusive by nature, this
// isn't.
export type ViolationWarning = 'seek' | 'speed' | null

// What a progress POST amounted to, once settled. Failures other than a
// multi-tab rejection are deliberately 'settled': they're recovered by the
// next post, not surfaced to the learner.
type ProgressPostOutcome = 'settled' | 'multiTabConflict'

export type UseVideoComplianceResult = {
  interruption: InterruptionState
  isPlayerInteractionBlocked: boolean
  violationWarning: ViolationWarning
  dismissViolationWarning: () => void
  handleTimeUpdate: (event: Event) => void
  handleRateChange: (event: Event) => void
  handleEnded: (event: Event) => void
  acknowledgeInsufficientProgress: () => void
  acknowledgePriorStepsIncomplete: () => void
  confirmContinuePlayback: () => void
}

// Compliance is a capability layered onto the plain VideoPlayer, not the
// player's identity — this hook owns every piece of anti-cheat and
// telemetry behavior (seek/rate gating, progress posting, completion
// verification, interruption dialogs) so that free-roam (ENG-433) can be
// implemented as attaching none of this, rather than as this same logic
// with checks stripped out at runtime.
export let useVideoCompliance = ({
  courseID,
  videoID,
  maxWatchedPositionSeconds,
  isStepCompleted,
  onCompleted,
  onProgressMade,
  onPriorStepsIncomplete,
  playerRef,
}: UseVideoComplianceOptions): UseVideoComplianceResult => {
  let [interruption, dispatchInterruption] = useReducer(interruptionReducer, {
    kind: 'idle',
  })
  let [violationWarning, setViolationWarning] = useState<ViolationWarning>(null)

  // Playback positions are read and written only inside event handlers —
  // nothing in render output depends on them — so they live in refs rather
  // than state. This avoids a re-render on every timeupdate tick (several
  // times a second during playback) and, because this hook is remounted
  // fresh per video (see VideoPlayer's key={activeStep.id} usage in
  // course-player.tsx), there's no reset effect or stale-id ref needed to
  // keep these in sync across video changes.
  //
  // Both refs hydrate from the same Redux-sourced prop, not from 0 — on
  // resume, the last valid position IS the max watched position (that's
  // what got the user here). Seeding lastValidPositionRef at 0 would make
  // the very next timeupdate after resume look like a huge forward skip
  // from 0 to wherever playback actually starts, and get wrongly clamped.
  let lastValidPositionRef = useRef(maxWatchedPositionSeconds)
  let maxWatchedPositionRef = useRef(maxWatchedPositionSeconds)
  // The furthest position the server has confirmed recording. Hydrated from
  // the same prop, which is the server's high-water mark across every
  // session and device the video has been watched on — not just this mount —
  // so a learner who resumes near the end and only plays the tail here still
  // gets that tail reported before completion, and nothing the server
  // already holds is re-sent.
  let lastRecordedPositionRef = useRef(maxWatchedPositionSeconds)
  // Progress POSTs that haven't settled yet. Completion waits for these: the
  // server judges completion against the pings it has received at that
  // moment, so one still in flight is as missing as one never sent.
  let pendingProgressPostsRef = useRef(new Set<Promise<ProgressPostOutcome>>())
  // Guards confirmContinuePlayback against starting a second countdown
  // interval (e.g. a fast double-click) and lets the unmount effect below
  // clear a still-running countdown so it can't dispatch into an unmounted
  // hook.
  let countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(
    () => () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
    },
    [],
  )

  let pausePlayer = () => playerRef.current?.pause()
  let playPlayer = () => playerRef.current?.play()

  let postProgress = (elapsedSeconds: number): Promise<ProgressPostOutcome> => {
    let post: Promise<ProgressPostOutcome> = postVideoProgress(
      courseID,
      videoID,
      elapsedSeconds,
    )
      .then((result): ProgressPostOutcome => {
        // A 204 means the server already considers this video complete
        // since the restart boundary — a no-op, not new progress to record.
        if (result === 'recorded') {
          lastRecordedPositionRef.current = Math.max(
            lastRecordedPositionRef.current,
            elapsedSeconds,
          )
          onProgressMade(elapsedSeconds)
        }
        return 'settled'
      })
      .catch((error): ProgressPostOutcome => {
        // .../progress/video only ever rejects with MultipleVideoStreamsDetected.
        // Anything else (network failure, 5xx) is left for the next post or
        // the pre-completion flush to cover.
        if (!getRejectionError(error)) return 'settled'
        pausePlayer()
        dispatchInterruption({ type: 'multiTabConflictDetected' })
        return 'multiTabConflict'
      })
      .finally(() => {
        pendingProgressPostsRef.current.delete(post)
      })
    pendingProgressPostsRef.current.add(post)
    return post
  }

  // Reports the furthest valid position reached, if the server doesn't
  // already have it, and waits for every progress POST still in flight. The
  // throttle above drops calls inside its window (trailing: false), so
  // without this the last stretch before the end — up to 20s — was never
  // reported, and completion could be judged before the final pings landed.
  let flushProgress = async (): Promise<ProgressPostOutcome> => {
    let finalPosition = maxWatchedPositionRef.current
    if (finalPosition > lastRecordedPositionRef.current) {
      postProgress(finalPosition)
    }
    let outcomes = await Promise.all(pendingProgressPostsRef.current)
    return outcomes.includes('multiTabConflict')
      ? 'multiTabConflict'
      : 'settled'
  }

  // A single throttle expresses the actual intent — post at most once per
  // 20 seconds of valid playback — rather than v1's floor(time) % 20 === 0
  // check layered with a separate ~1s debounce, which can miss or
  // double-fire a window depending on where timeupdate ticks land.
  let throttledPostProgress = useRef(
    throttle(postProgress, PROGRESS_POST_INTERVAL_MS, { trailing: false }),
  ).current

  let handleTimeUpdate = (event: Event) => {
    let player = event.target as MuxPlayerElement
    let { currentTime } = player
    let lastValidPosition = lastValidPositionRef.current
    let maxWatchedPosition = maxWatchedPositionRef.current

    let isValidProgression =
      currentTime <= maxWatchedPosition ||
      currentTime - lastValidPosition < MAX_FORWARD_SEEK_SECONDS

    if (!isValidProgression) {
      player.currentTime = lastValidPosition
      setViolationWarning('seek')
      return
    }

    lastValidPositionRef.current = currentTime
    let isBeyondMaxWatched = currentTime > maxWatchedPosition
    if (isBeyondMaxWatched) {
      maxWatchedPositionRef.current = currentTime
    }

    // Rehash gating: only progress beyond the previously-recorded max
    // position is new — replaying already-watched territory posts nothing.
    if (isBeyondMaxWatched) {
      throttledPostProgress(currentTime)
    }
  }

  let handleRateChange = (event: Event) => {
    let player = event.target as MuxPlayerElement
    if (player.playbackRate !== 1.0) {
      player.playbackRate = 1.0
      setViolationWarning('speed')
    }
  }

  let dismissViolationWarning = () => setViolationWarning(null)

  let requestCompletion = () =>
    postVideoStepCompleted(courseID, videoID)
      .then((result) => {
        // A 204 means this was already completed since the restart
        // boundary — a no-op, so there's nothing new to announce.
        if (result === 'recorded') onCompleted()
      })
      .catch((error) => {
        // .../stream-event/video can reject with MultipleVideoStreamsDetected,
        // PriorStepsNotCompleted, or InsufficientViewingEvidence.
        let rejection = getRejectionError(error)
        if (!rejection) return
        pausePlayer()
        switch (rejection.error) {
          case 'MultipleVideoStreamsDetected':
            dispatchInterruption({ type: 'multiTabConflictDetected' })
            break
          case 'PriorStepsNotCompleted':
            dispatchInterruption({ type: 'priorStepsIncompleteDetected' })
            break
          case 'InsufficientViewingEvidence':
            dispatchInterruption({ type: 'insufficientProgressDetected' })
            break
          default:
            break
        }
      })

  let handleEnded = (event: Event) => {
    let player = event.target as MuxPlayerElement
    let videoDuration = player.duration
    let lastValidPosition = lastValidPositionRef.current

    if (isStepCompleted) return

    let hasReachedThreshold =
      lastValidPosition >= videoDuration - COMPLETION_THRESHOLD_SECONDS
    if (!hasReachedThreshold) {
      player.currentTime = lastValidPosition
      return
    }

    flushProgress().then((outcome) => {
      // The conflict dialog is already up; completing now would only be
      // rejected for the same reason.
      if (outcome !== 'multiTabConflict') requestCompletion()
    })
  }

  let confirmContinuePlayback = () => {
    // Guard against a second countdown interval — e.g. a fast double-click
    // on "Continue" before the dialog closes — which would otherwise race
    // two intervals against the same reducer.
    if (countdownIntervalRef.current) return
    dispatchInterruption({ type: 'confirmContinuePlayback' })
    let secondsElapsed = 0
    countdownIntervalRef.current = setInterval(() => {
      secondsElapsed += 1
      if (secondsElapsed >= COUNTDOWN_SECONDS) {
        clearInterval(countdownIntervalRef.current ?? undefined)
        countdownIntervalRef.current = null
        dispatchInterruption({ type: 'countdownElapsed' })
        playPlayer()
        return
      }
      dispatchInterruption({ type: 'countdownTick' })
    }, 1000)
  }

  let acknowledgeInsufficientProgress = () => {
    dispatchInterruption({ type: 'acknowledgeInsufficientProgress' })
    // The dialog tells the user they'll be returned to their last verified
    // progress point — that's maxWatchedPositionSeconds (the prop this hook
    // was hydrated with, reflecting the last progress successfully posted
    // to the server), not 0. Resetting to 0 would rewind the whole video on
    // the next timeupdate instead of just disallowing the completion.
    lastValidPositionRef.current = maxWatchedPositionSeconds
    maxWatchedPositionRef.current = maxWatchedPositionSeconds
    let player = playerRef.current
    if (player) {
      player.currentTime = maxWatchedPositionSeconds
    }
  }

  // Unlike insufficient progress, this video's position isn't the problem —
  // resetting it would accomplish nothing. Send the user back to the first
  // incomplete step instead.
  let acknowledgePriorStepsIncomplete = () => {
    dispatchInterruption({ type: 'acknowledgePriorStepsIncomplete' })
    onPriorStepsIncomplete()
  }

  return {
    interruption,
    isPlayerInteractionBlocked: interruption.kind !== 'idle',
    violationWarning,
    dismissViolationWarning,
    handleTimeUpdate,
    handleRateChange,
    handleEnded,
    acknowledgeInsufficientProgress,
    acknowledgePriorStepsIncomplete,
    confirmContinuePlayback,
  }
}
